import PropTypes from 'prop-types';
import { createContext } from 'react';
// hooks
import useLocalStorage from '../hooks/useLocalStorage';
// theme
import palette from '../theme/palette';

// ----------------------------------------------------------------------

const PRIMARY_COLOR = [
  // DEFAULT — brand amber
  {
    name: 'default',
    ...palette.light.primary
  },
  // TEAL — brand teal
  {
    name: 'teal',
    lighter: '#CCFAF5',
    light: '#66EDDA',
    main: '#00DABB',
    dark: '#00A086',
    darker: '#006B5A',
    contrastText: '#514538'
  },
  // BROWN — brand dark brown as a deep earthy primary
  {
    name: 'brown',
    lighter: '#D4C9BF',
    light: '#8A7A6E',
    main: '#514538',
    dark: '#3A3028',
    darker: '#221C18',
    contrastText: '#fff'
  },
  // GREEN — semantic success green
  {
    name: 'green',
    lighter: '#D7F7B0',
    light: '#89E219',
    main: '#58CC02',
    dark: '#46A302',
    darker: '#357A02',
    contrastText: '#fff'
  },
  // RED
  {
    name: 'red',
    lighter: '#FFCECE',
    light: '#FF8585',
    main: '#FF4B4B',
    dark: '#CC3C3C',
    darker: '#992D2D',
    contrastText: '#fff'
  },
  // PURPLE
  {
    name: 'purple',
    lighter: '#EBD6FD',
    light: '#B985F4',
    main: '#7635dc',
    dark: '#431A9E',
    darker: '#200A69',
    contrastText: '#fff'
  }
];

SetColor.propTypes = {
  themeColor: PropTypes.oneOf(['default', 'teal', 'brown', 'green', 'red', 'purple'])
};

function SetColor(themeColor) {
  let color;
  const DEFAULT = PRIMARY_COLOR[0];
  const TEAL = PRIMARY_COLOR[1];
  const BROWN = PRIMARY_COLOR[2];
  const GREEN = PRIMARY_COLOR[3];
  const RED = PRIMARY_COLOR[4];
  const PURPLE = PRIMARY_COLOR[5];

  switch (themeColor) {
    case 'teal':
      color = TEAL;
      break;
    case 'brown':
      color = BROWN;
      break;
    case 'green':
      color = GREEN;
      break;
    case 'red':
      color = RED;
      break;
    case 'purple':
      color = PURPLE;
      break;
    default:
      color = DEFAULT;
  }
  return color;
}

const initialState = {
  themeMode: 'light',
  themeDirection: 'ltr',
  themeColor: 'default',
  themeStretch: false,
  onChangeMode: () => {},
  onChangeDirection: () => {},
  onChangeColor: () => {},
  onToggleStretch: () => {},
  setColor: PRIMARY_COLOR[0],
  colorOption: []
};

const SettingsContext = createContext(initialState);

SettingsProvider.propTypes = {
  children: PropTypes.node
};

function SettingsProvider({ children }) {
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: initialState.themeMode,
    themeDirection: initialState.themeDirection,
    themeColor: initialState.themeColor,
    themeStretch: initialState.themeStretch
  });

  const onChangeMode = (event) => {
    setSettings({
      ...settings,
      themeMode: event.target.value
    });
  };

  const onChangeDirection = (event) => {
    setSettings({
      ...settings,
      themeDirection: event.target.value
    });
  };

  const onChangeColor = (event) => {
    setSettings({
      ...settings,
      themeColor: event.target.value
    });
  };

  const onToggleStretch = () => {
    setSettings({
      ...settings,
      themeStretch: !settings.themeStretch
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        ...settings,
        // Mode
        onChangeMode,
        // Direction
        onChangeDirection,
        // Color
        onChangeColor,
        setColor: SetColor(settings.themeColor),
        colorOption: PRIMARY_COLOR.map((color) => ({
          name: color.name,
          value: color.main
        })),
        // Stretch
        onToggleStretch
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export { SettingsProvider, SettingsContext };
